import { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router';
import { Skeleton } from '@agentisai/design-system';
import { RequireAuth } from './RequireAuth';
import { RequireTenant } from './RequireTenant';
import { LoginPage } from '../pages/LoginPage';
import { HomePage } from '../pages/HomePage';
import { CallbackPage } from '../pages/CallbackPage';
import { WorkspacePlaceholder } from '../pages/WorkspacePlaceholder';
import { ProfilePage } from '../pages/ProfilePage';
import { EvalsPage } from '../pages/EvalsPage';
import { NotFoundPage } from '../pages/NotFoundPage';

// Agent Monitor (D15-06) is its own chunk: its pages bring the chart code, which
// the workspace never needs (frontend-architecture R16).
const MonitorRoutes = lazy(() => import('../pages/monitor/MonitorRoutes'));

/**
 * AppRouter — the S-E3 route table. Public: /login, /callback, * (404).
 * Protected (compose RequireAuth → RequireTenant): /, /profile (D13-07),
 * /monitor/* (Agent Monitor, D15-06, lazy) and /workspace/*. /evals (ADR-0072) is RequireAuth only — staff grants are not
 * tenant-scoped; the page and the API both check the grant. The real home and the workspace tree arrive in later
 * stories (F1 / §5.2); E3 ships placeholders behind the guards.
 */
export function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/callback" element={<CallbackPage />} />
      <Route
        path="/"
        element={
          <RequireAuth>
            <RequireTenant>
              <HomePage />
            </RequireTenant>
          </RequireAuth>
        }
      />
      <Route
        path="/profile"
        element={
          <RequireAuth>
            <RequireTenant>
              <ProfilePage />
            </RequireTenant>
          </RequireAuth>
        }
      />
      <Route
        path="/evals"
        element={
          // RequireAuth only: a staff grant is keyed by `sub` alone (ADR-0072 §1),
          // so a staff member with no tenant claim must still reach this page.
          <RequireAuth>
            <EvalsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/monitor/*"
        element={
          <RequireAuth>
            <RequireTenant>
              <Suspense fallback={<Skeleton shape="card" height={220} />}>
                <MonitorRoutes />
              </Suspense>
            </RequireTenant>
          </RequireAuth>
        }
      />
      <Route
        path="/workspace/*"
        element={
          <RequireAuth>
            <RequireTenant>
              <WorkspacePlaceholder />
            </RequireTenant>
          </RequireAuth>
        }
      />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
AppRouter.displayName = 'AppRouter';
